import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Ticket {
  id: string
  subject: string
  customer: string
  priority: string
  status: string
  category: string
  resolution_time_hours: number
  satisfaction_score: number
}

interface Agent {
  id: string
  name: string
  tickets_resolved: number
  avg_resolution_time: number
  satisfaction_score: number
  first_response_time: number
}

interface Category {
  id: string
  name: string
  ticket_count: number
  avg_resolution_time: number
  satisfaction_score: number
  trend: string
}

interface Statistics {
  total_tickets: number
  avg_resolution_time: number
  satisfaction_score: number
  first_response_time: number
  open_tickets: number
  resolved_today: number
}

export default function SupportAnalyticsPage() {
  const [tickets, setTickets] = useState<Ticket[]>([])
  const [agents, setAgents] = useState<Agent[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [ticketsRes, agentsRes, categoriesRes, statsRes] = await Promise.all([
        fetch('/api/supportanalytics/tickets'),
        fetch('/api/supportanalytics/agents'),
        fetch('/api/supportanalytics/categories'),
        fetch('/api/supportanalytics/statistics')
      ])
      if (ticketsRes.ok) setTickets(await ticketsRes.json())
      if (agentsRes.ok) setAgents(await agentsRes.json())
      if (categoriesRes.ok) setCategories(await categoriesRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching support analytics data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getPriorityBadge = (priority: string) => {
    if (priority === 'critical') return 'bg-red-100 text-red-800'
    if (priority === 'high') return 'bg-orange-100 text-orange-800'
    if (priority === 'medium') return 'bg-yellow-100 text-yellow-800'
    return 'bg-green-100 text-green-800'
  }

  const getStatusBadge = (status: string) => {
    if (status === 'open') return 'bg-blue-100 text-blue-800'
    if (status === 'in_progress') return 'bg-yellow-100 text-yellow-800'
    if (status === 'resolved') return 'bg-green-100 text-green-800'
    return 'bg-gray-100 text-gray-800'
  }

  const getSatisfactionColor = (score: number) => {
    if (score >= 4.5) return 'text-green-600'
    if (score >= 3.5) return 'text-yellow-600'
    return 'text-red-600'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Support Analytics</h1>
          <p className="mt-2 text-gray-600">Phase 227 - Support ticket and agent performance analytics</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Tickets</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_tickets}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Open Tickets</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.open_tickets}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Resolved Today</p>
              <p className="text-2xl font-bold text-green-600">{statistics.resolved_today}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Resolution</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.avg_resolution_time}h</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">First Response</p>
              <p className="text-2xl font-bold text-orange-600">{statistics.first_response_time}m</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">CSAT Score</p>
              <p className={`text-2xl font-bold ${getSatisfactionColor(statistics.satisfaction_score)}`}>
                {statistics.satisfaction_score}/5
              </p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Recent Tickets</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Resolution</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">CSAT</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {tickets.map((ticket) => (
                  <tr key={ticket.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{ticket.subject}</td>
                    <td className="px-6 py-4 text-gray-500">{ticket.customer}</td>
                    <td className="px-6 py-4 text-gray-500">{ticket.category}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getPriorityBadge(ticket.priority)}`}>
                        {ticket.priority}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(ticket.status)}`}>
                        {ticket.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-500">{ticket.resolution_time_hours}h</td>
                    <td className={`px-6 py-4 ${getSatisfactionColor(ticket.satisfaction_score)}`}>
                      {ticket.satisfaction_score}/5
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Agent Performance</h2>
            </div>
            <div className="p-6 space-y-4">
              {agents.map((agent) => (
                <div key={agent.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{agent.name}</span>
                    <span className={`text-lg font-bold ${getSatisfactionColor(agent.satisfaction_score)}`}>
                      {agent.satisfaction_score}/5
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Resolved</p>
                      <p className="font-medium text-green-600">{agent.tickets_resolved}</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Avg Time</p>
                      <p className="font-medium text-blue-600">{agent.avg_resolution_time}h</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">First Resp</p>
                      <p className="font-medium text-purple-600">{agent.first_response_time}m</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Categories</h2>
            </div>
            <div className="p-6 space-y-4">
              {categories.map((category) => (
                <div key={category.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{category.name}</span>
                    <span className={`text-sm ${category.trend === 'up' ? 'text-red-600' : category.trend === 'down' ? 'text-green-600' : 'text-gray-600'}`}>
                      {category.trend === 'up' ? '↑ Increasing' : category.trend === 'down' ? '↓ Decreasing' : '→ Stable'}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Tickets</p>
                      <p className="font-medium text-gray-900">{category.ticket_count}</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Avg Time</p>
                      <p className="font-medium text-blue-600">{category.avg_resolution_time}h</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">CSAT</p>
                      <p className={`font-medium ${getSatisfactionColor(category.satisfaction_score)}`}>
                        {category.satisfaction_score}/5
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
